import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { CheckCircle2, FileText, PenLine, Plus } from 'lucide-react'
import { listApplications, createApplication } from '../api/applicant'
import type { ApplicationWithDocuments } from '../api/types'
import { sectionBySlug } from '../apply/sections'
import { PageShell } from '../components/layout/PageShell'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4, ease: 'easeOut' as const },
  }),
}

/** Short uppercase prefix of the application id as the reference number. */
export function applicationReference(id: string): string {
  return `ZOT-${id.slice(0, 8).toUpperCase()}`
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function StatusBadge({ submitted }: { submitted: boolean }) {
  const color = submitted ? 'var(--color-success-text)' : 'var(--color-action-primary)'
  const bg = submitted ? 'var(--color-success-surface)' : 'var(--color-ai-badge-surface)'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        background: bg,
        border: `1px solid ${color}`,
        borderRadius: 'var(--radius-full)',
        padding: '4px 12px',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-label-sm)',
        fontWeight: 'var(--weight-semibold)',
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {submitted ? <CheckCircle2 size={13} aria-hidden="true" /> : <PenLine size={13} aria-hidden="true" />}
      {submitted ? 'Submitted' : 'In progress'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// MyApplicationsScreen — every application this email has, newest first.
// Submitted applications open a full read-only detail view; the in-progress
// draft resumes where it left off; and once everything is submitted a new
// application can be started (multiple applications per email).
// ---------------------------------------------------------------------------
export function MyApplicationsScreen() {
  const navigate = useNavigate()
  const [apps, setApps] = useState<ApplicationWithDocuments[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    listApplications()
      .then((res) => setApps(res.applications))
      .catch(() => setLoadFailed(true))
  }, [])

  const hasDraft = (apps ?? []).some((a) => a.status === 'in_progress')

  async function handleStartNew() {
    setStarting(true)
    try {
      await createApplication()
      // Full page load (not SPA navigation) so the session boot re-hydrates
      // the store from the fresh empty draft.
      window.location.assign('/apply/welcome')
    } catch {
      setStarting(false)
    }
  }

  return (
    <PageShell>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-8)',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-title-lg)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--color-text-primary)',
                margin: '0 0 var(--spacing-1) 0',
              }}
            >
              My Applications
            </h1>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-secondary)',
                margin: 0,
              }}
            >
              Every application submitted with this email address.
            </p>
          </div>
          {apps !== null && !hasDraft && (
            <Button
              variant="secondary"
              onClick={handleStartNew}
              loading={starting}
              style={{ gap: '6px' }}
            >
              <Plus size={16} aria-hidden="true" />
              Start new application
            </Button>
          )}
        </div>

        {/* Loading / error / empty states */}
        {apps === null && !loadFailed && (
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)' }}>
            Loading your applications…
          </p>
        )}
        {loadFailed && (
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-error-text)' }}>
            Something went wrong loading your applications. Please refresh the page.
          </p>
        )}
        {apps !== null && apps.length === 0 && (
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)' }}>
            You haven&rsquo;t started an application yet.
          </p>
        )}

        {/* Application cards */}
        {(apps ?? []).map((app, i) => {
          const fd = app.form_data ?? {}
          const submitted = app.status === 'submitted'
          const companyName = (fd.legalCompanyName as string) || 'Merchant application'
          const dateLine = submitted
            ? `Submitted ${formatDate(app.submitted_at)}`
            : `Started ${formatDate(app.created_at)} · Last saved ${formatDate(app.last_updated_at)}`
          return (
            <motion.div
              key={app.id}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              style={{ marginBottom: 'var(--spacing-4)' }}
            >
              <Card padding="lg">
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-3)',
                      flexWrap: 'wrap',
                      marginBottom: 'var(--spacing-2)',
                    }}
                  >
                    <h2
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-title-sm)',
                        fontWeight: 'var(--weight-semibold)',
                        color: 'var(--color-text-primary)',
                        margin: 0,
                      }}
                    >
                      {companyName}
                    </h2>
                    <StatusBadge submitted={submitted} />
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-body-sm)',
                      color: 'var(--color-text-secondary)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>Reference: {applicationReference(app.id)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{dateLine}</span>
                    {app.documents.length > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FileText size={13} aria-hidden="true" />
                          {app.documents.length} document{app.documents.length === 1 ? '' : 's'}
                        </span>
                      </>
                    )}
                  </p>
                </div>
                {/* Always its own row below the text block — a shared row with
                    the header (justify-content: space-between) let the button
                    stay inline for short titles but wrap onto a second row for
                    long ones, so identical cards looked inconsistent. */}
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  {submitted ? (
                    <Button variant="outline" onClick={() => navigate(`/applications/${app.id}`)}>
                      View application
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(sectionBySlug(app.current_section)?.route ?? '/welcome')}
                    >
                      Continue application
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          )
        })}
      </div>
    </PageShell>
  )
}
